import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Avatar,
  Grid,
  Divider,
  Alert,
  LinearProgress,
  IconButton,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  LocationOn,
  Edit,
  Save,
  Cancel,
  Visibility,
  VisibilityOff,
  Lock,
  Security,
  Notifications,
  Store,
  AttachMoney,
  CalendarToday,
  CheckCircle,
  Warning,
  Error,
  Info
} from '@mui/icons-material';
import { Calendar, MapPin, Shield, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const AdminProfile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    storeName: '',
    storeDescription: '',
    avatar: '',
    notifications: {
      email: true,
      sms: false,
      push: true
    },
    security: {
      twoFactor: false,
      loginAlerts: true
    }
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual API call
      const mockProfile = {
        name: user?.name || 'Nguyễn Văn C',
        email: user?.email || 'nguyenvanc@email.com',
        phone: '0123456789',
        address: '789 Đường DEF, Quận 3, TP.HCM',
        storeName: 'Admin Center',
        storeDescription: 'Trung tâm quản trị hệ thống chuyên nghiệp',
        avatar: user?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        notifications: {
          email: true,
          sms: false,
          push: true
        },
        security: {
          twoFactor: false,
          loginAlerts: true
        }
      };

      setProfile(mockProfile);
      // toast.success('Đã tải thông tin profile!');
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Có lỗi xảy ra khi tải thông tin profile');
      toast.error('Có lỗi xảy ra khi tải thông tin profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      // TODO: Implement API call to update profile
      setEditing(false);
      toast.success('Đã cập nhật thông tin profile!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Có lỗi xảy ra khi cập nhật profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Mật khẩu mới không khớp!');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Mật khẩu mới phải có ít nhất 6 ký tự!');
      return;
    }

    setLoading(true);
    try {
      // TODO: Implement API call to change password
      setShowPasswordDialog(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      toast.success('Đã thay đổi mật khẩu!');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Có lỗi xảy ra khi thay đổi mật khẩu');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = (type) => {
    setProfile(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: !prev.notifications[type]
      }
    }));
  };

  const handleSecurityChange = (type) => {
    setProfile(prev => ({
      ...prev,
      security: {
        ...prev.security,
        [type]: !prev.security[type]
      }
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle color="success" />;
      case 'pending':
        return <Warning color="warning" />;
      case 'suspended':
        return <Error color="error" />;
      default:
        return <Info color="info" />;
    }
  };

  return (
    <>
      {!loading && !error && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-800">Thông tin cá nhân</h3>
              <p className="text-sm text-gray-600 mt-1">Quản lý thông tin cá nhân và tài khoản</p>
            </div>
            <div className="flex items-center space-x-2">
              <Person className="text-purple-500" />
              <span className="text-sm text-gray-600">Admin</span>
            </div>
          </div>

          {/* Avatar Section */}
          <div className="text-center mb-6">
            <div className="relative inline-block">
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full mx-auto mb-4 flex items-center justify-center border-4 border-gray-100 shadow-lg">
                <span className="text-white font-bold text-2xl sm:text-3xl">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
            </div>
            
            {/* Tên hiển thị dưới ảnh */}
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              {user?.name || 'Admin Name'}
            </h2>
            
            {/* Ngày tham gia và vai trò trên cùng một hàng */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('vi-VN') : 'Chưa có thông tin'}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span>Quản trị</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{user?.email || 'Chưa có thông tin'}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số điện thoại
                </label>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{user?.phone || 'Chưa có thông tin'}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Additional Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Họ và tên
                </label>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Person className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{user?.name || 'Chưa có thông tin'}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ngày sinh
                </label>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">
                    {user?.birthDate ? new Date(user.birthDate).toLocaleDateString('vi-VN') : 'Chưa có thông tin'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Địa chỉ - Full width ở dưới cùng */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Địa chỉ
            </label>
            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <span className="text-gray-900">{user?.address || 'Chưa có thông tin'}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminProfile; 
